import { Colors } from '@blueprintjs/core'
import React from 'react'
import { upperCaseFirst } from 'change-case'
import { ShippingBoxPaddingChoice } from '../../../../current/types'
import { JoinedFormState, Requirements } from './types'

export const capitalize = string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const getBoxPaddingDefault = (paddingChoices: ShippingBoxPaddingChoice[]) => {
  if (!paddingChoices) return undefined

  return paddingChoices.find(choice => choice.default === true)?.value
}

export const parseBoxPadding = (boxPadding: string | number | null | undefined): number | null => {
  if (boxPadding != null && boxPadding !== '') {
    return parseFloat(String(boxPadding))
  }
  return null
}

export const hasItemDimensions = (product: { length?: unknown; width?: unknown; height?: unknown }): boolean => {
  return (
    product.length != null &&
    product.length !== '' &&
    product.width != null &&
    product.width !== '' &&
    product.height != null &&
    product.height !== ''
  )
}

export const applyDefaultBoxPadding = <T extends { boxPadding?: number | null }>(
  product: T,
  paddingChoices?: ShippingBoxPaddingChoice[]
): T => {
  if (product.boxPadding != null) {
    return product
  }

  const defaultBoxPaddingChoiceValue = getBoxPaddingDefault(paddingChoices)
  if (defaultBoxPaddingChoiceValue != null && defaultBoxPaddingChoiceValue !== '') {
    return { ...product, boxPadding: parseFloat(defaultBoxPaddingChoiceValue) }
  }

  return product
}

export const applyDefaultBoxPaddingIfDimensionsPresent = <
  T extends { boxPadding?: number | null; length?: unknown; width?: unknown; height?: unknown }
>(
  product: T,
  paddingChoices?: ShippingBoxPaddingChoice[]
): T => {
  if (!hasItemDimensions(product)) {
    return product
  }

  return applyDefaultBoxPadding(product, paddingChoices)
}

export const proccessNotes = (
  notes: { body: string }[],
  normalizeKeysToLowercase?: boolean
): Record<string, string> => {
  return notes.reduce(
    (acc, note) => {
      const lines = note.body.split('\n')
      if (lines[0] === 'Hammoq attributes:') {
        lines.slice(2).forEach(line => {
          const [key, value] = line.split(' - ')
          if (key && value) {
            acc[normalizeKeysToLowercase ? key.toLowerCase() : key] = value.replace(/(_|%%)/g, ' ')
          }
        })
      }
      return acc
    },
    {} as Record<string, string>
  )
}

export const getHammoqAttributesAutofilled = (specifics: { name: string }[], notes: { body: string }[]) => {
  const autofilled = specifics.reduce(
    (acc, specific) => {
      const specificName = specific.name.toLowerCase()
      for (const note of notes) {
        const attributes = proccessNotes([note])
        for (const key in attributes) {
          if (key.toLowerCase() === specificName) {
            acc[specific.name] = attributes[key]
          }
        }
      }
      return acc
    },
    {} as Record<string, string>
  )

  return autofilled
}

export const getHammoqAttributesTitleAutofilled = notes => {
  const proccessedNotes = proccessNotes(notes)
  const keys = ['Brand', 'Color', 'Style', 'Type', 'Size']
  return keys
    .map(key => proccessedNotes[key])
    .filter(v => v)
    .map(word => capitalize(word))
    .join(' ')
}

export const getHammoqAttributesDescriptionAutofilled = notes => {
  const proccessedNotes = proccessNotes(notes)
  if (Object.keys(proccessedNotes).length === 0) return null

  const outbound = Object.entries(proccessedNotes)
    .map(([key, value]) => `<b> ${key} :</b> ${value}`)
    .join('<br/><br/>')

  const title = getHammoqAttributesTitleAutofilled(notes)
  if (title) {
    return `<p> ${title} <br/><br/> ${outbound} </p>`
  }
  return `<p> ${outbound} </p>`
}

export const notFilledRequiredSpecifics = (ebayListing, ebayRequirements) => {
  let specificRequiredFields: any = []

  if (!ebayListing || !ebayRequirements) return specificRequiredFields

  if (ebayListing) {
    const requiredFields = ebayRequirements.specifics.filter(item => item.required).map(i => i.name)
    const notFilledRequiredFields = requiredFields.filter(item => !ebayListing.specifics[item])
    notFilledRequiredFields.forEach(fields => specificRequiredFields.push({ name: fields, value: null }))
    if (ebayRequirements.conditions.length > 0 && !ebayListing.condition) {
      specificRequiredFields = [...specificRequiredFields, { name: 'condition', value: null }]
    }

    // Check if required Condition Descriptors are not filled in, such as "Professional Grader" for graded trading cards.
    if (ebayRequirements.itemConditionPolicies) {
      const filledDescriptorIds = ebayListing.conditionDescriptors ? Object.keys(ebayListing.conditionDescriptors) : []
      const notFilledDescriptors = (ebayRequirements as Requirements).itemConditionPolicies
        .find(icp => icp.conditionId === ebayListing.condition)
        ?.conditionDescriptors.filter(cd => cd.conditionDescriptorConstraint.usage === 'REQUIRED')
        ?.filter(cd => !filledDescriptorIds.includes(cd.conditionDescriptorId))

      notFilledDescriptors?.forEach(cd => {
        specificRequiredFields.push({ name: cd.conditionDescriptorName, value: null })
      })
    }
  }
  return specificRequiredFields
}

export const renderError = error => {
  if (error) {
    let string: any
    if (error instanceof Array) {
      if (error.length === 0) {
        return null
      }

      string = error.join(', ')
    } else {
      string = error
    }
    return <span style={{ color: Colors.RED2 }}>{string}</span>
  }
  return null
}

// error strings that essentially mean "this field is missing"
const IS_REQUIRED_STRINGS = [
  'is required',
  "can't be blank",
  'is not a number',
  'is invalid', // this one is debatable, but I can't think of a reason it would be invalid besides being missing. See ebay "Listing Duration".
  'is too short (minimum is 1 character)', // product description
  'must exist' // shopify location
]

// Render the error(s), unless it's an error like "is required" AND we're already showing the "Required" labelInfo
export const renderErrorExceptRequired = (error, labelInfo) => {
  if (!error) {
    return null
  }

  let errors = error

  if (!(error instanceof Array)) {
    errors = [errors]
  }

  if (labelInfo && ['required', "req'd"].includes(labelInfo.toLowerCase())) {
    errors = errors.filter(s => !IS_REQUIRED_STRINGS.includes(s))
  }
  return renderError(errors)
}

// returns true if the string/array contains errors like 'is required' or similar
export const isRequiredError = error => {
  let errors = error

  if (!(error instanceof Array)) {
    errors = [errors]
  }

  return !!errors.find(s => IS_REQUIRED_STRINGS.includes(s))
}

// Try to show just "required" if given an error like "can't be blank", "is not a number", etc.
// but otherwise render the error string(s) as they are given
export const renderErrorButPreferRequired = (error: string | string[]) => {
  if (isRequiredError(error)) {
    return renderError('required')
  }
  return renderError(error)
}

export const stylizeTitle = (title, _error) => title

export const hasErrorsWithPrefix = (errors, prefix, whitelist = []) =>
  errors && Object.keys(errors).some(k => k.includes(prefix) && !whitelist.includes(k))

export const errorsFromPrefix = (errors, prefix) => {
  if (!errors) return ''

  const relevantErrors = []
  Object.keys(errors).forEach(k => {
    if (k.includes(prefix)) {
      relevantErrors.push(errors[k])
    }
  })

  return relevantErrors.join(', ')
}

export const listingTitleErrors = errors => {
  if (!errors) return {}

  const titleErrors = {}
  Object.keys(errors).forEach(k => {
    if (k.includes('ListingTitle')) {
      titleErrors[k] = errors[k]
    }
  })
  return titleErrors
}

export const listingTitleErrorString = errors => {
  const titleErrors = listingTitleErrors(errors)
  const keys = Object.keys(titleErrors)
  if (keys.length === 0) return null

  return keys
    .map(key => {
      const humanReadableKey = key.split('ListingTitle')[0]
      return `for ${upperCaseFirst(humanReadableKey)} the title ${titleErrors[key]}`
    })
    .sort()
    .join(', ')
}

interface ListingStateChange {
  [key: string]: any
}

interface SpecificsCombiner {
  currentState: JoinedFormState
  marketKey: string // 'ebayListing' or 'goodwillfindsListing'
  stateChanges: ListingStateChange
}

// If GoodwillFinds has a specific available for selection it will be returned from Requirements
const findGwfRequirementsMatchForEbaySpecificName = (ebaySpecificKey: string, currentState: JoinedFormState) => {
  // goodwillfindsListing specific name is actually 'slug', instead of the label name like eBay uses
  // e.g. handle conversion for `movie-tv-title` (gwf) to "Movie/TV Title" (ebay)
  return currentState.goodwillfindsRequirements.specifics.find(
    requirementsSpecific => ebaySpecificKey === requirementsSpecific.name
  )
}

export const transformEbaySpecificsChangesToGwf = ({
  currentState,
  stateChanges
}: Omit<SpecificsCombiner, 'marketKey'>): any => {
  const goodwillFindsSpecificsChanges = currentState.goodwillfindsListing.specifics || []

  const beforeSpecificsKeys = Object.keys(currentState.ebayListing?.specifics)
  const keysToChange = Object.keys(stateChanges.specifics)
  const deletedKeys = beforeSpecificsKeys.filter(k => stateChanges.specifics[k] === null)

  deletedKeys.forEach(keyToDelete => {
    const goodwillFindsRequirementMatch = findGwfRequirementsMatchForEbaySpecificName(keyToDelete, currentState)
    if (!goodwillFindsRequirementMatch) return

    const specificIndex = goodwillFindsSpecificsChanges
      .map(specific => specific.name)
      .indexOf(goodwillFindsRequirementMatch.slug)

    if (specificIndex > -1) {
      goodwillFindsSpecificsChanges.splice(specificIndex, 1)
    }
  })

  keysToChange.forEach(ebaySpecificKey => {
    const goodwillFindsRequirementMatch = findGwfRequirementsMatchForEbaySpecificName(ebaySpecificKey, currentState)
    if (!goodwillFindsRequirementMatch) return

    const specificsValue = stateChanges.specifics[ebaySpecificKey]
    const recommendedValueExists = !!goodwillFindsRequirementMatch.valueRecommendations?.find(
      recommendedValue => recommendedValue === specificsValue
    )
    if (!recommendedValueExists) return

    const specificIndex = goodwillFindsSpecificsChanges
      .map(specific => specific.name)
      .indexOf(goodwillFindsRequirementMatch.slug)

    if (specificIndex > -1) {
      goodwillFindsSpecificsChanges[specificIndex].value = specificsValue
    } else {
      goodwillFindsSpecificsChanges.push({
        name: goodwillFindsRequirementMatch.slug,
        value: specificsValue,
        required: goodwillFindsRequirementMatch.required
      })
    }
  })

  return goodwillFindsSpecificsChanges
}

export const combineSpecificsChanges = ({ currentState, marketKey, stateChanges }: SpecificsCombiner): any => {
  const additionalChanges: Partial<JoinedFormState> = {}
  if (
    marketKey === 'ebayListing' &&
    stateChanges.specifics &&
    currentState.goodwillfindsListing &&
    currentState.ebayRequirements?.specifics &&
    currentState.goodwillfindsRequirements?.specifics
  ) {
    const goodwillFindsSpecificsChanges = transformEbaySpecificsChangesToGwf({
      currentState,
      stateChanges
    })

    additionalChanges.goodwillfindsListing = {
      ...currentState.goodwillfindsListing,
      specifics: goodwillFindsSpecificsChanges
    }
  }
  return additionalChanges
}

export const USE_PRODUCT_VALUE = undefined
